import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Accessory } from '#pplx/frontend/aether/components/Accessory/index.js';
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { MenuItemBase } from '#pplx/frontend/aether/components/internal/menu/MenuItemBase.js';
import { MenuRadioGroupContext } from '#pplx/frontend/aether/components/internal/menu/MenuRadioGroupContext.js';
import { useMenuClose } from '#pplx/frontend/aether/components/internal/menu/useMenuContext.js';
import { DropdownMenuPrimitive } from '#pplx/frontend/aether/primitives/DropdownMenuPrimitive/index.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import { useCallback, useContext, useMemo } from 'react';
// Mobile: we own the checked state, so toggle `invisible` directly. The
// wrapper always renders so selection changes don't cause a horizontal shift.
const mobileCheckmark = (checked) => (_jsx("span", { className: classNames('text-super-primary', !checked && 'invisible'), "aria-hidden": !checked, children: _jsx(Accessory, { accessory: $icon('check') }) }));
// Desktop: let Radix drive checked state via `ItemIndicator`. `forceMount`
// keeps it rendered when unchecked so the row width stays stable; Radix sets
// `data-state="checked" | "unchecked"` on the slotted element and we hide the
// unchecked state via CSS.
const desktopCheckmark = () => (_jsx(DropdownMenuPrimitive.ItemIndicator, { forceMount: true, children: _jsx("span", { className: "text-super-primary data-[state=unchecked]:invisible", children: _jsx(Accessory, { accessory: $icon('check') }) }) }));
export function MenuRadioItem({ children, value, textValue, disabled, leadingAccessory, subtitle, badge, badgeVariant, trailingAccessory, ...rest }) {
    const { isMobileStyle } = useAetherContext();
    const radioGroupContext = useContext(MenuRadioGroupContext);
    const closeMobileMenu = useMenuClose();
    // Mobile: Check if context exists and calculate checked state
    if (isMobileStyle && !radioGroupContext) {
        throw new Error('Menu.RadioItem must be used within Menu.RadioGroup');
    }
    const checked = radioGroupContext?.value === value;
    const closeOnSelect = radioGroupContext?.closeOnSelect ?? false;
    const handleDesktopSelect = useCallback((event) => {
        if (!closeOnSelect) {
            // Prevents menu from closing
            event.preventDefault();
        }
    }, [closeOnSelect]);
    const handleMobileClick = useCallback(() => {
        if (disabled || !radioGroupContext) {
            return;
        }
        radioGroupContext.onValueChange(value);
        if (closeOnSelect) {
            closeMobileMenu?.();
        }
    }, [disabled, radioGroupContext, value, closeOnSelect, closeMobileMenu]);
    // Mobile: Keyboard handler
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            if (e.key === ' ') {
                e.preventDefault(); // Prevent scrolling on Space key
            }
            handleMobileClick();
        }
    }, [handleMobileClick]);
    // Mobile omits the unchecked checkmark when an accessory is present so the
    // accessory can occupy the trailing edge.
    const mobileTrailingAccessory = useMemo(() => {
        if (!trailingAccessory) {
            return mobileCheckmark(checked);
        }
        return (_jsxs("span", { className: "gap-4 flex items-center", children: [trailingAccessory, checked ? mobileCheckmark(true) : null] }));
    }, [checked, trailingAccessory]);
    // Desktop keeps the checkmark mounted beside the accessory so the accessory
    // stays in the same position when selection changes.
    const desktopTrailingAccessory = useMemo(() => trailingAccessory ? (_jsxs("span", { className: "gap-4 flex items-center", children: [desktopCheckmark(), trailingAccessory] })) : (desktopCheckmark()), [trailingAccessory]);
    // Mobile renders within a dialog (i.e: bottom sheet) so
    // we don't need to wrap within an accessible menu item
    // and just render as standard radio button.
    if (isMobileStyle) {
        return (_jsx(MenuItemBase, { leadingAccessory: leadingAccessory, trailingAccessory: mobileTrailingAccessory, subtitle: subtitle, badge: badge, badgeVariant: badgeVariant, role: "radio", "aria-checked": checked, tabIndex: disabled ? -1 : 0, onClick: handleMobileClick, onKeyDown: handleKeyDown, "aria-disabled": disabled, ...rest, children: children }));
    }
    // Desktop: Use the Aether dropdown-menu radio-item primitive.
    return (_jsx(DropdownMenuPrimitive.RadioItem, { value: value, disabled: disabled, onSelect: handleDesktopSelect, textValue: textValue, className: "focus:outline-hidden", ...rest, children: _jsx(MenuItemBase, { leadingAccessory: leadingAccessory, trailingAccessory: desktopTrailingAccessory, subtitle: subtitle, badge: badge, badgeVariant: badgeVariant, children: children }) }));
}
//# sourceMappingURL=MenuRadioItem.js.map